import {Box, Card, Button, IconButton, Collapse} from "@mui/material";
import Form from "react-bootstrap/Form";
import React, {useEffect, useState, useRef} from "react";
import TextField from "@mui/material/TextField";
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {zoneService} from "../service/ZoneService";
import Autocomplete from "@mui/material/Autocomplete";
import {communeService} from "../service/CommuneService";
import {Canton} from "../utils/Canton";
import {landService} from "../service/LandService";
import {addressService} from "../service/AddressService";

export const LandsMapFilterCard = ({handleSearch, defaultFilters, isMapReady, resetTrigger}) => {
    const [autocompleteOptions, setAutocompleteOptions] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [landsCodeNumber, setLandsCodeNumber] = useState(defaultFilters?.no_parcelle ?? "");

    const [zoneAffectation, setZoneAffectation] = useState(defaultFilters?.zoneAffectation ?? []);
    const [zones, setZones] = useState([]);
    const [communes, setCommunes] = useState([]);

    const [addressOptions, setAddressOptions] = useState([]);
    const [address, setAddress] = useState(defaultFilters?.address ?? "");
    const [addressInputValue, setAddressInputValue] = useState(
        typeof (defaultFilters?.address) === 'object' ? defaultFilters.address.address_full : (defaultFilters?.address ?? "")
    );

    const [owner, setOwner] = useState("");
    const [canton, setCanton] = useState(defaultFilters?.canton ?? "");
    const [areaMin, setAreaMin] = useState("");
    const [areaMax, setAreaMax] = useState("");
    const [minYear, setMinYear] = useState("");
    const [maxYear, setMaxYear] = useState("");
    const [minBuildingNbr, setMinBuildingNbr] = useState("");
    const [maxBuildingNbr, setMaxBuildingNbr] = useState("");
    const [showAllFields, setShowAllFields] = useState(false);

    const cantonOptions = Object.values(Canton);

    const initialFilterApplied = useRef(false);
    const filterButtonRef = useRef(null);

    const cantonLabels: Record<string, string> = {
        GE: "Genève",
        VD: "Vaud",
    };

    const filteredZones = React.useMemo(() => {
        if (!canton) return zones;

        const selectedCantonEntry = Object.values(Canton).find(c => c.valeur === canton);
        const codePourFiltrage = selectedCantonEntry ? selectedCantonEntry.code : "";

        return zones.filter(z => z.canton === codePourFiltrage);
    }, [zones, canton]);

    useEffect(() => {
        const fetchZones = async () => {
            try {
                const zonesData: { nomZone: string; cantons: string[] }[] = await zoneService.getAllZones();

                if (!Array.isArray(zonesData)) {
                    console.warn("⚠️ [fetchZones] Format inattendu :", zonesData);
                    setZones([]);
                    return;
                }

                const zonesGE: { label: string; canton: string }[] = [];
                const zonesVD: { label: string; canton: string }[] = [];

                zonesData.forEach(zone => {
                    if (!zone.cantons || zone.cantons.length === 0) {
                        zonesGE.push({ label: zone.nomZone, canton: "GE" });
                        zonesVD.push({ label: zone.nomZone, canton: "VD" });
                    } else {
                        if (zone.cantons.includes("GE")) zonesGE.push({ label: zone.nomZone, canton: "GE" });
                        if (zone.cantons.includes("VD")) zonesVD.push({ label: zone.nomZone, canton: "VD" });
                    }
                });
                const optionsForAutocompleteOrdered = [...zonesGE, ...zonesVD];

                setZones(optionsForAutocompleteOrdered);
            } catch (error) {
                console.error("❌ Erreur lors de la récupération des zones :", error);
                setZones([]);
            }
        };
        fetchZones();
    }, []);

    useEffect(() => {
        const fetchCommunes = async () => {
            try {
                const communesData = await communeService.getAllCommunes();
                if (Array.isArray(communesData)) {
                    setCommunes(communesData);
                } else {
                    setCommunes([]);
                }
            } catch (error) {
                console.error("Erreur récupération communes :", error);
                setCommunes([]);
            }
        };

        fetchCommunes();
    }, []);

    useEffect(() => {
        const fetchCombinedAutocomplete = async () => {
            if (!inputValue || inputValue.trim().length < 2) {
                setAutocompleteOptions([]);
                return;
            }

            try {
                const searchLower = inputValue.toLowerCase().trim();
                const matchedCommunes = communes
                    .filter(c => c.name && c.name.toLowerCase().includes(searchLower))
                    .map(c => ({
                        type: "Commune",
                        name: c.name,
                        canton: c.canton,
                        displayLabel: `${c.name} (${cantonLabels[c.canton] || c.canton})`
                    }));

                const landResults = await landService.autocomplete(inputValue);
                const formattedLands = (landResults || []).map(l => ({
                    ...l,
                    type: "Parcelle",
                    displayLabel: `${l.no_parcelle} ${l.commune_nom || ""}`
                }));

                setAutocompleteOptions([...matchedCommunes, ...formattedLands]);
            } catch (error) {
                console.error("Erreur lors de l'autocomplétion combinée :", error);
                setAutocompleteOptions([]);
            }
        };

        const timeoutId = setTimeout(fetchCombinedAutocomplete, 300);
        return () => clearTimeout(timeoutId);
    }, [inputValue, communes]);

    useEffect(() => {
        const fetchAddressAutocomplete = async () => {
            if (!addressInputValue || addressInputValue.trim().length < 2) {
                setAddressOptions([]);
                return;
            }

            try {
                const results = await addressService.searchAddress(addressInputValue);
                setAddressOptions(Array.isArray(results) ? results : []);
            } catch (error) {
                console.error("Erreur lors de la recherche d'adresse :", error);
                setAddressOptions([]);
            }
        };

        const timeoutId = setTimeout(fetchAddressAutocomplete, 300);
        return () => clearTimeout(timeoutId);
    }, [addressInputValue]);

    useEffect(() => {
        if (defaultFilters) {
            if (defaultFilters.no_parcelle) {
                const parcelObj = {
                    type: "Parcelle",
                    no_parcelle: defaultFilters.no_parcelle,
                    commune_nom: defaultFilters.commune || "",
                    displayLabel: `${defaultFilters.no_parcelle} ${defaultFilters.commune || ""}`.trim()
                };
                setLandsCodeNumber(parcelObj);
                setInputValue(parcelObj.displayLabel);
            } else if (defaultFilters.commune) {
                // On cherche la vraie commune dans le tableau `communes` (comme dans l'autocomplétion)
                const matchedCommune = communes.find(c =>
                    c.name && c.name.toLowerCase() === defaultFilters.commune.toLowerCase() &&
                    (!defaultFilters.canton || c.canton === defaultFilters.canton)
                );

                const communeObj = matchedCommune ? {
                    type: "Commune",
                    name: matchedCommune.name,
                    canton: matchedCommune.canton,
                    displayLabel: `${matchedCommune.name} (${cantonLabels[matchedCommune.canton] || matchedCommune.canton})`
                } : {
                    // Fallback si la commune n'est pas encore chargée ou trouvée dans le tableau
                    type: "Commune",
                    name: defaultFilters.commune,
                    canton: defaultFilters.canton || "",
                    displayLabel: defaultFilters.commune
                };

                setLandsCodeNumber(communeObj);
                setInputValue(communeObj.displayLabel);
            }

            setCanton(defaultFilters.canton || "");
            setAddress(defaultFilters.address || "");

            if (defaultFilters.address) {
                setAddressInputValue(typeof defaultFilters.address === 'object' ? defaultFilters.address.address_full : defaultFilters.address);
            }

            setZoneAffectation(defaultFilters.zoneAffectation || []);
        }
    }, [defaultFilters, communes]);

    useEffect(() => {
        if (!isMapReady || initialFilterApplied.current) {
            return;
        }

        if (defaultFilters && !defaultFilters.isEmpty) {
            console.log("Auto-applying filters:", defaultFilters);

            setTimeout(() => {
                if (filterButtonRef.current) {
                    filterButtonRef.current.click();
                } else {
                    const filters = {
                        no_parcelle: defaultFilters.no_parcelle || "",
                        commune: defaultFilters.commune || "",
                        zoneAffectation: defaultFilters.zoneAffectation || [],
                        canton: defaultFilters.canton || "",
                        address: defaultFilters.address || "",
                        isEmpty: false
                    };
                    handleSearch(filters);
                }
                initialFilterApplied.current = true;
            }, 300);
        }
    }, [isMapReady, defaultFilters, handleSearch]);

    useEffect(() => {
        if (resetTrigger > 0) {
            onClickClean();
        }
    }, [resetTrigger]);


    const onClickValidate = () => {
        const isFiltersEmpty = !(
            landsCodeNumber ||
            canton ||
            address ||
            (zoneAffectation && zoneAffectation.length > 0) ||
            owner ||
            areaMin ||
            areaMax ||
            minYear ||
            maxYear ||
            minBuildingNbr ||
            maxBuildingNbr
        );

        if (isFiltersEmpty) {
            console.log("Filtres vides : déclenchement de la réinitialisation");
            onClickClean();
            return;
        }

        let newFilters = { isEmpty: false };

        if (landsCodeNumber) {
            if (typeof landsCodeNumber === 'object') {
                if (landsCodeNumber.type === "Commune") {
                    newFilters.commune = landsCodeNumber.name;
                } else {
                    newFilters.no_parcelle = landsCodeNumber.no_parcelle;
                    if (landsCodeNumber.commune_nom) {
                        newFilters.commune = landsCodeNumber.commune_nom;
                    }
                }
            } else {
                newFilters.no_parcelle = landsCodeNumber;
            }
        }

        if (address) {
            if (typeof address === 'object') {
                newFilters.address = address.address_full;
            } else {
                newFilters.address = address;
            }
        }

        if (zoneAffectation) newFilters.zoneAffectation = zoneAffectation;
        if (owner) newFilters.owner = owner;
        if (canton) newFilters.canton = canton;
        if (areaMin) newFilters.areaMin = Number(areaMin);
        if (areaMax) newFilters.areaMax = Number(areaMax);
        if (maxYear) newFilters.maxYear = maxYear;
        if (minYear) newFilters.minYear = minYear;
        if (minBuildingNbr) newFilters.minBuildingNbr = minBuildingNbr;
        if (maxBuildingNbr) newFilters.maxBuildingNbr = maxBuildingNbr;

        handleSearch(newFilters);
    };

    const onClickClean = () => {
        setAreaMin("");
        setAreaMax("");
        setMaxYear("");
        setMinYear("");
        setOwner("");
        setCanton("");
        setAddress("");
        setAddressInputValue("");
        setZoneAffectation([]);
        setLandsCodeNumber("");
        setInputValue("");
        setMinBuildingNbr("");
        setMaxBuildingNbr("");

        handleSearch({isEmpty: true});
    };

    return (
        <>
            <Card
                className="shadow-none"
                style={{padding: "20px", backgroundColor: "#fff", height: "100%", zIndex: "99999999", overflow: "auto"}}
            >
                <Box component="form">
                    {/* Recherche globale combinée (Commune + Parcelle) */}
                    <Form.Group className="my-3" controlId="1" key="landsCodeNumber">
                        <Autocomplete
                            controlId="1"
                            freeSolo
                            options={autocompleteOptions}
                            filterOptions={(options) => options}
                            value={landsCodeNumber}
                            onChange={(event, newValue) => {
                                setLandsCodeNumber(newValue);
                                if (newValue && typeof newValue === 'object') {
                                    setInputValue(newValue.displayLabel || "");
                                } else if (typeof newValue === 'string') {
                                    setInputValue(newValue);
                                } else {
                                    setInputValue("");
                                }
                            }}
                            inputValue={inputValue}
                            onInputChange={(event, newInputValue, reason) => {
                                setInputValue(newInputValue);
                                if (reason === 'reset' && !newInputValue) {
                                    setLandsCodeNumber("");
                                }
                            }}
                            groupBy={(option) => {
                                if (typeof option === 'string') return "Recherche libre";
                                return option.type === "Commune" ? "Communes" : "Parcelles";
                            }}
                            getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.displayLabel || "";
                            }}
                            isOptionEqualToValue={(option, value) => {
                                if (!value) return false;
                                if (option.type === "Commune" && value.type === "Commune") {
                                    return option.name === value.name;
                                }
                                return option.land_id === value.land_id;
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Recherche globale"
                                    placeholder="ex: 7448 Bernex"
                                    size="small"
                                />
                            )}
                        />
                    </Form.Group>

                    <IconButton
                        onClick={() => setShowAllFields(!showAllFields)}
                        sx={{"&:hover": {background: "none"}, p: 0, mb: 1}}
                    >
                        {showAllFields ? <><span style={{fontSize: "14px"}}>Afficher moins</span>
                            <ExpandLessIcon/> </> : <><span
                            style={{fontSize: "14px"}}>Filtre avancé</span><ExpandMoreIcon/></>}
                    </IconButton>
                    <Collapse in={showAllFields} timeout="auto" unmountOnExit>
                        <Form.Group className="my-3" controlId="3" key="address">
                            <Autocomplete
                                fullWidth
                                freeSolo
                                options={addressOptions}
                                value={address}
                                onChange={(event, newValue) => {
                                    setAddress(newValue);
                                    if (newValue && typeof newValue === 'object') {
                                        setAddressInputValue(newValue.address_full || "");
                                    } else if (typeof newValue === 'string') {
                                        setAddressInputValue(newValue);
                                    } else {
                                        setAddressInputValue("");
                                    }
                                }}
                                inputValue={addressInputValue}
                                onInputChange={(event, newInputValue, reason) => {
                                    setAddressInputValue(newInputValue);
                                    if (reason === 'reset' && !newInputValue) {
                                        setAddress("");
                                    }
                                }}
                                getOptionLabel={(option) => {
                                    if (typeof option === 'string') return option;
                                    return option.address_full || "";
                                }}
                                isOptionEqualToValue={(option, value) => {
                                    if (!value) return false;
                                    if (typeof value === 'string') return option.address_full === value;
                                    return option.land_id === value.land_id;
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        controlId="3"
                                        label="Adresse"
                                        placeholder="ex: Avenue Breton 1, Agiez"
                                        size="small"
                                    />
                                )}
                            />
                        </Form.Group>
                        <Form.Group className="my-3" controlId="51" key="Canton">
                            <Autocomplete
                                fullWidth
                                options={cantonOptions}
                                getOptionLabel={(option) => option.valeur}
                                isOptionEqualToValue={(option, value) =>
                                    option.valeur === value.valeur
                                }
                                value={
                                    cantonOptions.find((c) => c.valeur === canton) || null
                                }
                                onChange={(event, newValue) => {
                                    setCanton(newValue ? newValue.valeur : "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Canton"
                                        placeholder="Canton"
                                        size="small"
                                        fullWidth
                                    />
                                )}
                            />
                        </Form.Group>
                        <Form.Group className="my-3" controlId="52" key="zoneAffectation">
                            <Autocomplete
                                multiple
                                fullWidth
                                options={filteredZones}
                                value={zoneAffectation}
                                onChange={(event, newValue) => setZoneAffectation(newValue)}
                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                groupBy={(option) => cantonLabels[option.canton] || option.canton}
                                getOptionLabel={(option) => option.label || ""}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Zone d'affectation"
                                        placeholder={canton ? `Zones pour ${canton}` : "Sélectionner une zone"}
                                        size="small"
                                    />
                                )}
                            />
                        </Form.Group>
                        <Form.Group className="mt-3 mb-2 " controlId="6" key="owner">
                            <TextField
                                type="text"
                                controlId="6"
                                label="Propriétaire"
                                placeholder="Propriétaire"
                                fullWidth
                                value={owner}
                                onChange={(e) => setOwner(e.target.value)}
                                size="small"
                            />
                        </Form.Group>

                        <Form.Group className="my-1" controlId="61" key="building_nbr">
                            <Form.Label>Nombre de batiment</Form.Label>
                            <Box sx={{display: "flex", justifyContent: "space-between"}}>
                                <TextField
                                    type="number"
                                    controlId="6"
                                    label="min"
                                    placeholder="min"
                                    InputProps={{inputProps: {min: 1}}}
                                    fullWidth
                                    value={minBuildingNbr}
                                    onChange={(e) => setMinBuildingNbr(e.target.value)}
                                    sx={{mb: 1, width: "47%"}}
                                    size="small"
                                />
                                <TextField
                                    type="number"
                                    controlId="6"
                                    label="max"
                                    placeholder="max"
                                    InputProps={{inputProps: {min: 2}}}
                                    fullWidth
                                    value={maxBuildingNbr}
                                    onChange={(e) => setMaxBuildingNbr(e.target.value)}
                                    sx={{width: "47%"}}
                                    size="small"
                                />
                            </Box>
                        </Form.Group>
                        <Form.Group className="my-1" controlId="6" key="building_year">
                            <Form.Label>Date de construction</Form.Label>
                            <Box sx={{display: "flex", justifyContent: "space-between"}}>
                                <TextField
                                    type="number"
                                    controlId="6"
                                    label="min"
                                    placeholder="min"
                                    InputProps={{inputProps: {min: 1}}}
                                    fullWidth
                                    value={minYear}
                                    onChange={(e) => setMinYear(e.target.value)}
                                    sx={{mb: 1, width: "47%"}}
                                    size="small"
                                />
                                <TextField
                                    type="number"
                                    controlId="6"
                                    label="max"
                                    placeholder="max"
                                    InputProps={{inputProps: {min: 2}}}
                                    fullWidth
                                    value={maxYear}
                                    onChange={(e) => setMaxYear(e.target.value)}
                                    sx={{width: "47%"}}
                                    size="small"
                                />
                            </Box>
                        </Form.Group>
                        <Form.Group controlId="6" key="area">
                            <Form.Label>Surface parcelle</Form.Label>
                            <Box sx={{display: "flex", justifyContent: "space-between"}}>
                                <TextField
                                    type="number"
                                    controlId="6"
                                    label="min"
                                    placeholder="min"
                                    InputProps={{inputProps: {min: 1}}}
                                    fullWidth
                                    value={areaMin}
                                    onChange={(e) => setAreaMin(e.target.value)}
                                    sx={{mb: 1, width: "47%"}}
                                    size="small"
                                />
                                <TextField
                                    type="number"
                                    controlId="6"
                                    label="max"
                                    placeholder="max"
                                    InputProps={{inputProps: {min: 2}}}
                                    fullWidth
                                    value={areaMax}
                                    onChange={(e) => setAreaMax(e.target.value)}
                                    sx={{width: "47%"}}
                                    size="small"
                                />
                            </Box>
                        </Form.Group>
                    </Collapse>

                    <Box sx={{display: "flex", justifyContent: "flex-end", pt: 2}}>

                        <Button
                            sx={{
                                background: "#0D0D0D",
                                color: "#fff",
                                "&:hover": {background: "#757575"},
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                verticalAlign: "center",
                                fontWeight: 500
                            }}
                            onClick={() => onClickClean()}
                        >
                            <RefreshIcon sx={{mr: 1, fontSize: 18}}/>
                            REINITIALISER
                        </Button>
                        <Button
                            ref={filterButtonRef}
                            sx={{
                                background: "#1B208C",
                                color: "#fff",
                                "&:hover": {background: "#363636"},
                                ml: 1,
                            }}
                            onClick={() => onClickValidate()}
                        >
                            Filtrer
                        </Button>
                    </Box>
                </Box>
            </Card>
        </>
    );
};